const MAX_SHEET_NAME_LENGTH = 31;
const MAX_COLUMN_NAME_LENGTH = 50;
const INVALID_CHARS_REGEX = /[^a-zA-Z0-9\s_-]/;
const COLUMN_INVALID_CHARS_REGEX = /[:\\/?*[\]]/;
const APOSTROPHE = "'";
const RESERVED_NAME = 'history';

/**
 * Validates Excel sheet name according to Excel rules:
 * - Max 31 characters
 * - Only alphanumeric, spaces, dashes, underscores
 * - Cannot be "History" (case-insensitive)
 * @param {string} name
 * @returns {string|null} Error message or null if valid
 */
export const validateSheetName = name => {
  const trimmed = name.trim();

  if (trimmed.length > MAX_SHEET_NAME_LENGTH) {
    return `Sheet name must be ${MAX_SHEET_NAME_LENGTH} characters or fewer`;
  }

  if (INVALID_CHARS_REGEX.test(trimmed)) {
    return 'Sheet name can only contain letters, numbers, spaces, dashes, and underscores';
  }

  if (trimmed.toLowerCase() === RESERVED_NAME) {
    return 'Sheet name cannot be "History"';
  }

  return null;
};

/**
 * Validates a renamed column name. Mirrors the Excel sheet-name forbidden
 * character set, plus apostrophe edge rules.
 * @param {string} name
 * @returns {string|null} Error message or null if valid
 */
export const validateColumnName = name => {
  const trimmed = name.trim();

  if (trimmed.length > MAX_COLUMN_NAME_LENGTH) {
    return `Column name must be ${MAX_COLUMN_NAME_LENGTH} characters or fewer`;
  }

  if (COLUMN_INVALID_CHARS_REGEX.test(trimmed)) {
    return 'Column name cannot contain : \\ / ? * [ or ]';
  }

  if (trimmed.startsWith(APOSTROPHE) || trimmed.endsWith(APOSTROPHE)) {
    return 'Column name cannot start or end with an apostrophe';
  }

  return null;
};

/**
 * Creates a validator that checks both basic column name rules and uniqueness.
 * @param {string[]} otherColumnNames Display names of other columns to check against
 * @returns {function(string): string|null} Validator function
 */
export const createColumnNameValidator = otherColumnNames => name => {
  const basicError = validateColumnName(name);
  if (basicError) return basicError;

  const trimmedLower = name.trim().toLowerCase();
  if (!trimmedLower) return null;

  if (
    otherColumnNames.some(other => other.trim().toLowerCase() === trimmedLower)
  ) {
    return 'Column name already exists';
  }

  return null;
};

/**
 * Creates a validator that checks both basic sheet name rules and uniqueness.
 * @param {string[]} otherSheetNames Names of other sheets to check against
 * @returns {function(string): string|null} Validator function
 */
export const createSheetNameValidator = otherSheetNames => name => {
  const basicError = validateSheetName(name);
  if (basicError) return basicError;

  const trimmedLower = name.trim().toLowerCase();
  if (
    otherSheetNames.some(other => other.trim().toLowerCase() === trimmedLower)
  ) {
    return 'Sheet name already exists';
  }

  return null;
};
